import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';
import { distinctUntilChanged, map } from 'rxjs/operators';

/**
 * Wer meldet, dass die Seite hinter dem Startbild steht.
 *
 *   `daten`  - die Reiter haben ihre Zahlen, die Seite ist sichtbar bedienbar
 *   `fehler` - Anmeldung oder erster Aufruf gescheitert; dann muss das Startbild
 *              SOFORT weg, sonst verdeckt es die Meldung bis zur Obergrenze
 */
export type SplashSignal = 'daten' | 'fehler';

/**
 * Fertig-Signale für das Startbild.
 *
 * Das Startbild liegt über der Anwendung, die darunter von der ersten Zeile an
 * lädt. `AppComponent` nimmt es weg, sobald BEIDES gilt: der Ablauf ist einmal
 * durch (Mindestdauer) und hier ist ein Signal angekommen.
 *
 * `BehaviorSubject` löst nebenbei das Problem, das in den React-Vorlagen eigens
 * behandelt werden musste: Ein Signal, das VOR dem Abonnieren kommt, geht nicht
 * verloren.
 */
@Injectable({ providedIn: 'root' })
export class SplashReadyService {
  private readonly gemeldet = new Set<SplashSignal>();
  private readonly signale$ = new BehaviorSubject<Set<SplashSignal>>(this.gemeldet);

  /**
   * „Das steht" - mehrfach aufrufen ist harmlos.
   *
   * ⚠️ Nicht aus einem Getter oder aus der Vorlage heraus melden, sondern aus
   * `ngOnInit`, aus einem `tap()` im Datenstrom oder aus einem NgRx-Effekt.
   * Sonst gibt es `ExpressionChangedAfterItHasBeenCheckedError`.
   */
  melden(signal: SplashSignal): void {
    if (this.gemeldet.has(signal)) {
      return;
    }
    this.gemeldet.add(signal);
    this.signale$.next(this.gemeldet);
  }

  /** true, sobald irgendeines der Signale da ist. */
  bereit$(): Observable<boolean> {
    return this.signale$.pipe(
      map((signale) => signale.has('daten') || signale.has('fehler')),
      distinctUntilChanged()
    );
  }

  /**
   * Nur das Fehlersignal - es hebt die Mindestdauer auf.
   *
   * Sonst verdeckt das Startbild eine gescheiterte Anmeldung noch volle 2.8 s,
   * und der Benutzer sieht die Meldung erst, wenn die Geschichte zu Ende
   * erzählt ist. Bei einem Fehler gibt es nichts mehr zu erzählen.
   */
  fehler$(): Observable<boolean> {
    return this.signale$.pipe(
      map((signale) => signale.has('fehler')),
      distinctUntilChanged()
    );
  }
}
